import {
  createRegistrationHost,
  spawnRegistered,
  type BodyHandle,
  type BodyType,
  type Box3D,
  type ContactBeginEventWithShapes,
  type ShapeHandle,
  type ShapeIdentity,
  type Vec3,
  type World,
  type WorldOptions,
} from 'box3d-web';

import {
  PhysicsEntityRegistry,
  type PhysicsEntityRegistrySnapshot,
  type PhysicsEntityAddress,
  type RegisteredShape,
  type WorldActor,
} from './physicsEntityRegistry';
import type { ElementIdentity, LayerIdentity, StructureIdentity, WorldIdentity } from './identities';

export interface DestructibleWallDefinition {
  readonly structureId: StructureIdentity;
  readonly elementId: ElementIdentity;
  readonly layerId: LayerIdentity;
  readonly materialId: string;
  readonly position: Vec3;
  readonly halfExtents: Vec3;
  readonly bodyType?: BodyType;
  readonly density?: number;
  readonly friction?: number;
  readonly restitution?: number;
}

export interface WallSnapshot {
  readonly structureId: StructureIdentity;
  readonly elementId: ElementIdentity;
  readonly layerId: LayerIdentity;
  readonly materialId: string;
  readonly body: BodyHandle;
  readonly shape: ShapeHandle;
  readonly shapeIdentity: ShapeIdentity;
  readonly disposed: boolean;
}

export interface ContactRoute {
  readonly event: ContactBeginEventWithShapes;
  /**
   * Stable semantic pair key used to order routes. This is independent of
   * native handles, shape indices, generations, and callback order.
   * If multiple routes have the same semantic key, consumers must group them
   * order-independently until shape-level semantic ordinals exist.
   */
  readonly routeKey: string;
  /** Participants retain the event's original shapeA/shapeB alignment. */
  readonly participants: readonly [RegisteredShape | undefined, RegisteredShape | undefined];
}

export interface SharedDestructionWorldSnapshot {
  readonly registry: PhysicsEntityRegistrySnapshot;
  readonly walls: readonly WallSnapshot[];
  readonly hosts: readonly StructureHostSnapshot[];
  readonly worldActors: readonly WorldActorSnapshot[];
  readonly nativeBodyCount: number;
}

export interface StructureHostSnapshot {
  readonly structureId: StructureIdentity;
  readonly revision: number;
  readonly bodies: readonly BodyHandle[];
  readonly shapes: readonly ShapeIdentity[];
  readonly disposed: boolean;
}

export interface WorldActorSnapshot {
  readonly body: BodyHandle;
  readonly shape: ShapeHandle;
  readonly shapeIdentity: ShapeIdentity;
  readonly address: PhysicsEntityAddress;
  readonly disposed: boolean;
}

export interface WorldActorDefinition {
  readonly actor: WorldActor;
  readonly ordinal: number;
  readonly position: Vec3;
  readonly halfExtents: Vec3;
  readonly materialId?: string;
  readonly bodyType?: BodyType;
  readonly density?: number;
  readonly friction?: number;
  readonly restitution?: number;
}

interface NativeWallState {
  readonly body: BodyHandle;
  readonly shape: ShapeHandle;
  readonly shapeIdentity: ShapeIdentity;
}

interface NativeWorldActorState {
  readonly body: BodyHandle;
  readonly shape: ShapeHandle;
  readonly shapeIdentity: ShapeIdentity;
  readonly address: PhysicsEntityAddress;
}

function copyVec3(value: Vec3, label: string): Vec3 {
  if (!Array.isArray(value) || value.length !== 3 || value.some((entry) => !Number.isFinite(entry) || entry <= 0 && label === 'halfExtents')) {
    throw new RangeError(`${label} must contain three finite values`);
  }
  return Object.freeze([value[0], value[1], value[2]]) as unknown as Vec3;
}

function copyDefinition(definition: DestructibleWallDefinition): DestructibleWallDefinition {
  if (typeof definition.materialId !== 'string' || definition.materialId.trim().length === 0) {
    throw new TypeError('materialId must be a non-empty string');
  }
  if (definition.density !== undefined && (!Number.isFinite(definition.density) || definition.density <= 0)) {
    throw new RangeError('density must be finite and positive');
  }
  if (definition.friction !== undefined && (!Number.isFinite(definition.friction) || definition.friction < 0)) {
    throw new RangeError('friction must be finite and non-negative');
  }
  if (definition.restitution !== undefined && (!Number.isFinite(definition.restitution) || definition.restitution < 0)) {
    throw new RangeError('restitution must be finite and non-negative');
  }
  return Object.freeze({
    structureId: definition.structureId,
    elementId: definition.elementId,
    layerId: definition.layerId,
    materialId: definition.materialId,
    position: copyVec3(definition.position, 'position'),
    halfExtents: copyVec3(definition.halfExtents, 'halfExtents'),
    ...(definition.bodyType === undefined ? {} : { bodyType: definition.bodyType }),
    ...(definition.density === undefined ? {} : { density: definition.density }),
    ...(definition.friction === undefined ? {} : { friction: definition.friction }),
    ...(definition.restitution === undefined ? {} : { restitution: definition.restitution }),
  });
}

function copyShapeIdentity(identity: ShapeIdentity): ShapeIdentity {
  return Object.freeze({
    index1: identity.index1,
    world0: identity.world0,
    generation: identity.generation,
  });
}

function semanticAddressKey(registration: RegisteredShape | undefined): string {
  if (registration === undefined) return 'unresolved';
  const address = registration.bodyRegistration.address;
  if (address.scope === 'world') {
    return `world:${address.worldId.key}:${address.actor}:${address.ordinal}`;
  }
  const hierarchy = [
    address.structureId.key,
    address.elementId?.key,
    address.layerId?.key,
    address.cellId?.key,
    address.fragmentId?.key,
  ].filter((key): key is string => key !== undefined);
  return `structure:${hierarchy.join('/')}:material=${address.materialId ?? ''}`;
}

/** Build a stable route key from semantic participants only. */
export function canonicalContactRouteKey(
  participants: readonly [RegisteredShape | undefined, RegisteredShape | undefined],
): string {
  const participantsKey = [
    semanticAddressKey(participants[0]),
    semanticAddressKey(participants[1]),
  ].sort().join('|');
  return participantsKey;
}

/** One independently resettable structure runtime in a shared physics world. */
export class DestructionWallRuntime {
  private disposed = false;
  private native: NativeWallState;
  private readonly owner: SharedDestructionWorld;
  readonly definition: DestructibleWallDefinition;

  constructor(
    owner: SharedDestructionWorld,
    definition: DestructibleWallDefinition,
    native: NativeWallState,
  ) {
    this.owner = owner;
    this.definition = definition;
    this.native = native;
  }

  get structureId(): StructureIdentity {
    return this.definition.structureId;
  }

  get body(): BodyHandle {
    return this.native.body;
  }

  get shape(): ShapeHandle {
    return this.native.shape;
  }

  get shapeIdentity(): ShapeIdentity {
    return this.native.shapeIdentity;
  }

  reset(): void {
    this.assertLive();
    this.owner.resetWall(this);
  }

  dispose(): void {
    if (this.disposed) return;
    this.owner.disposeWall(this);
    this.disposed = true;
  }

  snapshot(): WallSnapshot {
    return Object.freeze({
      structureId: this.definition.structureId,
      elementId: this.definition.elementId,
      layerId: this.definition.layerId,
      materialId: this.definition.materialId,
      body: this.native.body,
      shape: this.native.shape,
      shapeIdentity: this.native.shapeIdentity,
      disposed: this.disposed,
    });
  }

  replaceNative(native: NativeWallState): void {
    this.native = native;
  }

  markDisposed(): void {
    this.disposed = true;
  }

  assertLive(): void {
    if (this.disposed) throw new Error('destruction wall used after dispose()');
  }
}

/**
 * Registration boundary for a structure whose native bodies are created by a
 * separate runtime (for example, DemolitionRuntime).  The host owns only the
 * bodies registered through it; it never exposes registry mutation to callers.
 */
export class DestructionStructureHost {
  private disposed = false;
  private readonly owner: SharedDestructionWorld;
  private readonly inner: ReturnType<typeof createRegistrationHost>;
  readonly structureId: StructureIdentity;

  constructor(owner: SharedDestructionWorld, structureId: StructureIdentity) {
    this.owner = owner;
    this.structureId = structureId;
    this.inner = createRegistrationHost({
      world: owner.world,
      registry: owner.registry as unknown as Parameters<typeof createRegistrationHost>[0]['registry'],
      participantKey: structureId.key,
      belongsToHost: (address) => {
        const entity = address as PhysicsEntityAddress;
        return entity.scope === 'structure' && entity.structureId.key === structureId.key;
      },
    });
  }

  get revision(): number {
    return this.inner.revision;
  }

  registerBody(body: BodyHandle, address: Extract<PhysicsEntityAddress, { scope: 'structure' }>): void {
    this.assertLive();
    if (address.structureId.key !== this.structureId.key) {
      throw new Error('structure body address does not belong to this host');
    }
    this.inner.registerBody(body, address);
  }

  registerShape(shape: ShapeHandle, body: BodyHandle): RegisteredShape {
    this.assertLive();
    return this.inner.registerShape(shape, body) as RegisteredShape;
  }

  /** Remove semantic ownership after the native owner destroys the body. */
  unregisterBody(body: BodyHandle): boolean {
    return this.inner.unregisterBody(body);
  }

  destroyBody(body: BodyHandle): boolean {
    return this.inner.destroyBody(body);
  }

  /** Mark one successful structure reset; bodies are managed by the runtime. */
  advanceRevision(): number {
    this.assertLive();
    return this.inner.advanceRevision();
  }

  snapshot(): StructureHostSnapshot {
    this.assertLive();
    const snapshot = this.inner.snapshot();
    return Object.freeze({
      structureId: this.structureId,
      revision: snapshot.revision,
      bodies: snapshot.bodies,
      shapes: snapshot.shapes,
      disposed: snapshot.disposed,
    });
  }

  dispose(): void {
    if (this.disposed) return;
    let firstError: unknown;
    try {
      this.inner.dispose();
    } catch (error) {
      firstError = error;
    } finally {
      this.owner.releaseStructureHost(this);
      this.markDisposed();
    }
    if (firstError !== undefined) throw firstError;
  }

  markDisposed(): void {
    this.disposed = true;
  }

  assertLive(): void {
    if (this.disposed) throw new Error('destruction structure host used after dispose()');
  }
}

export class WorldActorRuntime {
  private disposed = false;
  private readonly owner: SharedDestructionWorld;
  private readonly native: NativeWorldActorState;

  constructor(owner: SharedDestructionWorld, native: NativeWorldActorState) {
    this.owner = owner;
    this.native = native;
  }

  get body(): BodyHandle { return this.native.body; }
  get shape(): ShapeHandle { return this.native.shape; }
  get shapeIdentity(): ShapeIdentity { return this.native.shapeIdentity; }

  snapshot(): WorldActorSnapshot {
    return Object.freeze({ ...this.native, disposed: this.disposed, shapeIdentity: copyShapeIdentity(this.native.shapeIdentity) });
  }

  dispose(): void {
    if (this.disposed) return;
    this.owner.disposeWorldActor(this);
    this.disposed = true;
  }

  markDisposed(): void { this.disposed = true; }
}

/**
 * Page-agnostic owner of one Box3D module/world and multiple structure runtimes.
 * It owns native world/module teardown; callers own only semantic definitions.
 */
export class SharedDestructionWorld {
  readonly world: World;
  readonly registry: PhysicsEntityRegistry;
  readonly worldId: WorldIdentity;
  private readonly box3d: Box3D;
  private readonly structures = new Map<string, DestructionWallRuntime>();
  private readonly hosts = new Map<string, DestructionStructureHost>();
  private readonly worldActors = new Map<string, WorldActorRuntime>();
  private readonly pendingWorldActors = new Map<number, string>();
  private readonly nextWorldActorOrdinals = new Map<WorldActor, number>();
  private disposed = false;

  constructor(
    box3d: Box3D,
    worldId: WorldIdentity,
    worldOptions: WorldOptions = {},
  ) {
    this.box3d = box3d;
    this.worldId = worldId;
    this.registry = new PhysicsEntityRegistry(worldId);
    this.world = box3d.createWorld(worldOptions);
  }

  createWall(definition: DestructibleWallDefinition): DestructionWallRuntime {
    this.assertLive();
    const normalized = copyDefinition(definition);
    if (normalized.structureId.parent?.key !== this.worldId.key) {
      throw new Error('wall structure identity belongs to a different world');
    }
    if (this.structures.has(normalized.structureId.key) || this.hosts.has(normalized.structureId.key)) {
      throw new Error(`structure ${normalized.structureId.key} is already registered`);
    }
    const native = this.createNative(normalized);
    const wall = new DestructionWallRuntime(this, normalized, native);
    this.structures.set(normalized.structureId.key, wall);
    return wall;
  }

  getWall(structureId: StructureIdentity): DestructionWallRuntime | undefined {
    this.assertLive();
    return this.structures.get(structureId.key);
  }

  createStructureHost(structureId: StructureIdentity): DestructionStructureHost {
    this.assertLive();
    if (structureId.parent?.key !== this.worldId.key) {
      throw new Error('structure identity belongs to a different world');
    }
    if (this.structures.has(structureId.key) || this.hosts.has(structureId.key)) {
      throw new Error(`structure ${structureId.key} is already registered`);
    }
    const host = new DestructionStructureHost(this, structureId);
    this.hosts.set(structureId.key, host);
    return host;
  }

  getStructureHost(structureId: StructureIdentity): DestructionStructureHost | undefined {
    this.assertLive();
    return this.hosts.get(structureId.key);
  }

  getWorldActor(actor: WorldActor, ordinal: number): WorldActorRuntime | undefined {
    this.assertLive();
    return this.worldActors.get(`${actor}:${ordinal}`);
  }

  /** Reserve a process-local monotonic ordinal for one actor kind. */
  allocateWorldActorOrdinal(actor: WorldActor): number {
    this.assertLive();
    const ordinal = this.nextWorldActorOrdinals.get(actor) ?? 0;
    this.nextWorldActorOrdinals.set(actor, ordinal + 1);
    return ordinal;
  }

  /** Register an externally-created world-owned body before its shape exists. */
  registerWorldBody(
    body: BodyHandle,
    address: Extract<PhysicsEntityAddress, { scope: 'world' }>,
  ): void {
    this.assertLive();
    const key = `${address.actor}:${address.ordinal}`;
    if (this.worldActors.has(key) || [...this.pendingWorldActors.values()].includes(key)) {
      throw new Error(`world actor ${key} is already registered`);
    }
    // registerBody validates before mutating; subsequent Map writes cannot
    // fail. Avoid broad rollback here because a duplicate-handle error must
    // never remove the pre-existing valid registration.
    this.registry.registerBody(body, address);
    this.pendingWorldActors.set(body as number, key);
    this.noteWorldActorOrdinal(address.actor, address.ordinal);
  }

  /** Register the exact native identity of an externally-created actor shape. */
  registerWorldShape(shape: ShapeHandle, body: BodyHandle): WorldActorRuntime {
    this.assertLive();
    const key = this.pendingWorldActors.get(body as number);
    if (key === undefined) throw new Error('world actor body is not pending registration');
    const address = this.registry.resolveBody(body)?.address;
    if (address === undefined || address.scope !== 'world') throw new Error('world actor body registration is invalid');
    try {
      const identity = this.world.getShapeIdentity(shape);
      if (identity === null) throw new Error('Box3D returned no shape identity');
      this.registry.registerShape(shape, body, identity);
      const actor = new WorldActorRuntime(this, { body, shape, shapeIdentity: copyShapeIdentity(identity), address });
      this.worldActors.set(key, actor);
      this.pendingWorldActors.delete(body as number);
      return actor;
    } catch (error) {
      this.unregisterWorldBody(body);
      throw error;
    }
  }

  /** Remove semantic registration after a caller has destroyed the body. */
  unregisterWorldBody(body: BodyHandle): boolean {
    const removed = this.registry.unregisterBody(body);
    this.pendingWorldActors.delete(body as number);
    for (const [key, actor] of this.worldActors) {
      if (actor.body === body) {
        this.worldActors.delete(key);
        actor.markDisposed();
        break;
      }
    }
    return removed;
  }

  /** Create and register a shared world actor (currently used for the ground). */
  createWorldActor(definition: WorldActorDefinition): WorldActorRuntime {
    this.assertLive();
    const key = `${definition.actor}:${definition.ordinal}`;
    if (this.worldActors.has(key) || [...this.pendingWorldActors.values()].includes(key)) {
      throw new Error(`world actor ${key} is already registered`);
    }
    const position = copyVec3(definition.position, 'position');
    const halfExtents = copyVec3(definition.halfExtents, 'halfExtents');
    const address: Extract<PhysicsEntityAddress, { scope: 'world' }> = {
      scope: 'world',
      worldId: this.worldId,
      actor: definition.actor,
      ordinal: definition.ordinal,
      ...(definition.materialId === undefined ? {} : { materialId: definition.materialId }),
    };
    const body = this.world.createBody({
      type: definition.bodyType ?? 'static',
      position,
    });
    try {
      const shape = this.world.addBox(body, halfExtents, {
        ...(definition.density === undefined ? {} : { density: definition.density }),
        ...(definition.friction === undefined ? {} : { friction: definition.friction }),
        ...(definition.restitution === undefined ? {} : { restitution: definition.restitution }),
      });
      const identity = this.world.getShapeIdentity(shape);
      if (identity === null) throw new Error('Box3D returned no shape identity');
      this.registry.registerBody(body, address);
      try {
        this.registry.registerShape(shape, body, identity);
      } catch (error) {
        this.registry.unregisterBody(body);
        throw error;
      }
      const actor = new WorldActorRuntime(this, { body, shape, shapeIdentity: copyShapeIdentity(identity), address });
      this.worldActors.set(key, actor);
      this.noteWorldActorOrdinal(address.actor, address.ordinal);
      return actor;
    } catch (error) {
      try { this.registry.unregisterBody(body); } catch { /* rollback best effort */ }
      try { this.world.destroyBody(body); } catch { /* rollback best effort */ }
      throw error;
    }
  }

  /** Return the canonical shared ground, creating it once when absent. */
  ensureGround(): WorldActorRuntime {
    this.assertLive();
    const existing = this.worldActors.get('ground:0');
    if (existing !== undefined) return existing;
    return this.createWorldActor({
      actor: 'ground',
      ordinal: 0,
      position: [0, -0.6, 0],
      halfExtents: [60, 0.6, 60],
      density: 0,
      friction: 0.9,
      restitution: 0.02,
    });
  }

  resetWall(wall: DestructionWallRuntime): void {
    this.assertLive();
    wall.assertLive();
    if (this.structures.get(wall.structureId.key) !== wall) {
      throw new Error('wall is not owned by this destruction world');
    }
    this.world.destroyBody(wall.body);
    const removed = this.registry.unregisterStructure(wall.structureId);
    if (removed !== 1) throw new Error(`expected one body while resetting ${wall.structureId.key}, got ${removed}`);
    try {
      wall.replaceNative(this.createNative(wall.definition));
    } catch (error) {
      // The old native state and registry entry are already gone. If the
      // replacement cannot be created, remove the wall from the live graph and
      // fail closed rather than exposing a runtime with dead handles.
      this.structures.delete(wall.structureId.key);
      wall.markDisposed();
      throw error;
    }
  }

  disposeWall(wall: DestructionWallRuntime): void {
    this.assertLive();
    if (this.structures.get(wall.structureId.key) !== wall) return;
    this.world.destroyBody(wall.body);
    const removed = this.registry.unregisterStructure(wall.structureId);
    if (removed !== 1) throw new Error(`expected one body while disposing ${wall.structureId.key}, got ${removed}`);
    this.structures.delete(wall.structureId.key);
    wall.markDisposed();
  }

  disposeWorldActor(actor: WorldActorRuntime): void {
    const key = [...this.worldActors.entries()].find(([, value]) => value === actor)?.[0];
    if (key === undefined) return;
    this.world.destroyBody(actor.body);
    this.unregisterWorldBody(actor.body);
  }

  releaseStructureHost(host: DestructionStructureHost): void {
    if (this.hosts.get(host.structureId.key) === host) this.hosts.delete(host.structureId.key);
  }

  drainContactBegins(): readonly ContactRoute[] {
    this.assertLive();
    const events = this.world.drainContactBeginEventsWithShapes();
    const routes = events.map((event) => {
      const participants = Object.freeze([
        this.registry.resolveShape(event.shapeA),
        this.registry.resolveShape(event.shapeB),
      ]) as unknown as readonly [RegisteredShape | undefined, RegisteredShape | undefined];
      return Object.freeze({
        event,
        routeKey: canonicalContactRouteKey(participants),
        participants,
      });
    });
    routes.sort((left, right) => left.routeKey < right.routeKey ? -1 : left.routeKey > right.routeKey ? 1 : 0);
    return Object.freeze(routes);
  }

  snapshot(): SharedDestructionWorldSnapshot {
    this.assertLive();
    const walls = [...this.structures.values()]
      .map((wall) => wall.snapshot())
      .sort((left, right) => left.structureId.key < right.structureId.key ? -1 : left.structureId.key > right.structureId.key ? 1 : 0);
    const hosts = [...this.hosts.values()]
      .map((host) => host.snapshot())
      .sort((left, right) => left.structureId.key < right.structureId.key ? -1 : left.structureId.key > right.structureId.key ? 1 : 0);
    const worldActors = [...this.worldActors.values()]
      .map((actor) => actor.snapshot())
      .sort((left, right) => {
        const l = left.address.scope === 'world' ? `${left.address.actor}:${left.address.ordinal}` : '';
        const r = right.address.scope === 'world' ? `${right.address.actor}:${right.address.ordinal}` : '';
        return l.localeCompare(r);
      });
    return Object.freeze({
      registry: this.registry.snapshot(),
      walls: Object.freeze(walls),
      hosts: Object.freeze(hosts),
      worldActors: Object.freeze(worldActors),
      nativeBodyCount: this.world.bodyCount(),
    });
  }

  nativeBodyCount(): number {
    this.assertLive();
    return this.world.bodyCount();
  }

  dispose(): void {
    if (this.disposed) return;
    const walls = [...this.structures.values()];
    const hosts = [...this.hosts.values()];
    const actors = [...this.worldActors.values()];
    let firstError: unknown;
    const attempt = (operation: () => void) => {
      try {
        operation();
      } catch (error) {
        if (firstError === undefined) firstError = error;
      }
    };
    for (const wall of walls) attempt(() => wall.dispose());
    for (const host of hosts) attempt(() => host.dispose());
    for (const actor of actors) attempt(() => actor.dispose());
    attempt(() => this.registry.clear());
    attempt(() => this.world.destroy());
    attempt(() => this.box3d.dispose());
    this.structures.clear();
    this.hosts.clear();
    this.worldActors.clear();
    this.pendingWorldActors.clear();
    this.nextWorldActorOrdinals.clear();
    for (const wall of walls) wall.markDisposed();
    for (const host of hosts) host.markDisposed();
    for (const actor of actors) actor.markDisposed();
    this.disposed = true;
    if (firstError !== undefined) throw firstError;
  }

  private noteWorldActorOrdinal(actor: WorldActor, ordinal: number): void {
    const next = this.nextWorldActorOrdinals.get(actor) ?? 0;
    if (ordinal >= next) this.nextWorldActorOrdinals.set(actor, ordinal + 1);
  }

  private createNative(definition: DestructibleWallDefinition): NativeWallState {
    const material = {
      ...(definition.density === undefined ? {} : { density: definition.density }),
      ...(definition.friction === undefined ? {} : { friction: definition.friction }),
      ...(definition.restitution === undefined ? {} : { restitution: definition.restitution }),
    };
    const spawned = spawnRegistered(this.world, this.registry as unknown as Parameters<typeof spawnRegistered>[1], {
      body: { type: definition.bodyType ?? 'dynamic', position: definition.position },
      bodyAddress: {
        scope: 'structure',
        worldId: this.worldId,
        structureId: definition.structureId,
        elementId: definition.elementId,
        layerId: definition.layerId,
        materialId: definition.materialId,
      },
      shapes: [{
        kind: 'box',
        half: definition.halfExtents,
        ...(Object.keys(material).length === 0 ? {} : { material }),
      }],
    });
    const spawnedShape = spawned.shapes[0];
    if (spawnedShape === undefined) throw new Error('Box3D returned no shape identity');
    return {
      body: spawned.body,
      shape: spawnedShape.shape,
      shapeIdentity: copyShapeIdentity(spawnedShape.identity),
    };
  }

  private assertLive(): void {
    if (this.disposed) throw new Error('destruction world used after dispose()');
  }
}
